import { useEffect, useRef, useState } from "react";

export default function ActionToast({ message, tone = "success", duration = 5200, onDismiss }) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const onDismissRef = useRef(onDismiss);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!message) {
      setEntered(false);
      const clearTimer = window.setTimeout(() => setMounted(false), 220);
      return () => window.clearTimeout(clearTimer);
    }

    setMounted(true);
    setEntered(false);
    const enterFrame = window.requestAnimationFrame(() => setEntered(true));
    const exitTimer = window.setTimeout(() => {
      setEntered(false);
      if (onDismissRef.current) {
        window.setTimeout(() => onDismissRef.current?.(), 220);
      }
    }, duration);

    return () => {
      window.cancelAnimationFrame(enterFrame);
      window.clearTimeout(exitTimer);
    };
  }, [duration, message]);

  if (!message || !mounted) return null;

  const isError = tone === "error";
  const accent = isError ? "#ef4444" : "#22c55e";

  return (
    <div
      role="status"
      aria-live={isError ? "assertive" : "polite"}
      style={{
        ...styles.shell,
        ...(entered ? styles.shellEntered : {}),
        background: isError
          ? "linear-gradient(135deg, rgba(255,255,255,0.64), rgba(254,226,226,0.36))"
          : "linear-gradient(135deg, rgba(255,255,255,0.64), rgba(220,252,231,0.36))",
        borderColor: isError ? "rgba(239,68,68,0.24)" : "rgba(34,197,94,0.24)",
        boxShadow: isError ? "0 18px 54px rgba(127,29,29,0.16)" : "0 18px 54px rgba(20,83,45,0.14)",
        color: isError ? "#7f1d1d" : "#14532d",
      }}
    >
      <span style={{ ...styles.accent, background: accent, boxShadow: `0 0 22px ${accent}66` }} />
      <span style={styles.message}>{message}</span>
      {onDismiss ? (
        <button type="button" aria-label="Dismiss notification" onClick={onDismiss} style={styles.dismissButton}>
          x
        </button>
      ) : null}
    </div>
  );
}

const styles = {
  shell: {
    alignItems: "center",
    backdropFilter: "blur(34px) saturate(1.55)",
    border: "1px solid",
    borderRadius: 999,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "9px minmax(0, 1fr) auto",
    lineHeight: 1.25,
    maxWidth: "min(620px, calc(100vw - 28px))",
    minHeight: 42,
    opacity: 0,
    padding: "7px 10px 7px 11px",
    pointerEvents: "auto",
    position: "fixed",
    right: 14,
    top: 14,
    transform: "translateX(calc(100% + 48px))",
    transition: "transform 320ms cubic-bezier(.22,1,.36,1), opacity 220ms ease",
    WebkitBackdropFilter: "blur(34px) saturate(1.55)",
    zIndex: 10000,
  },
  shellEntered: {
    opacity: 1,
    transform: "translateX(0)",
  },
  accent: {
    alignSelf: "center",
    borderRadius: 999,
    height: 9,
    width: 9,
  },
  message: {
    fontSize: 13,
    fontWeight: 900,
    minWidth: 0,
  },
  dismissButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.36)",
    border: "1px solid rgba(255,255,255,0.54)",
    borderRadius: 999,
    color: "inherit",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 12,
    fontWeight: 900,
    height: 24,
    justifyContent: "center",
    lineHeight: 1,
    width: 24,
  },
};
